/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import type { XCSSAllProperties, XCSSAllPseudos } from '@compiled/react';

import { cssMap, cx, jsx, type StrictXCSSProp } from '@atlaskit/css';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type LoadingStatus, StatusIndicator } from '../../../common/ui/loading-status-indicator';

const styles = cssMap({
	root: {
		width: '100%',
		columnGap: token('space.050'),
		justifyContent: 'flex-start',
		// font styles
		color: token('color.text.subtle'),
		font: token('font.body'),
	},
	titleContainer: {
		display: 'flex',
		flexDirection: 'row',
		flex: 1,
		alignItems: 'center',
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		paddingBottom: token('space.100'),
	},
	iconContainer: {
		paddingTop: token('space.100'),
		marginTop: token('space.025'),
		display: 'flex',
		flexDirection: 'column',
	},
	contentContainer: {
		flexDirection: 'column',
		flex: 1,
	},
	verticalRule: {
		width: '0px',
		minHeight: '12px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		display: 'flex',
		flex: 1,
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
	},
	loadingStepIconPlaceholder: {
		width: '20px',
	},
});

export type LoadingStepProps = {
	testId?: string;
	/** main text content of the loading step  */
	title?: string;
	/** determines the status icon displayed */
	status: LoadingStatus;
	isLastItem?: boolean;
	/**
	 * determines if a 20px icon placeholder is rendered or not.
	 * this keeps width equal when there are both expandable and non-expandable sections combined
	 **/
	placeholder?: boolean;
};

type LoadingStepContainerProps = {
	/** determines the status icon displayed */
	status: LoadingStatus;
	testId?: string;
	children: React.ReactNode;
	xcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
	isLastItem: boolean;
};

export const LoadingStepContainer = ({
	testId,
	children,
	xcss,
	status,
	isLastItem,
}: LoadingStepContainerProps) => {
	return (
		<Flex xcss={cx(styles.root, xcss)} testId={testId}>
			<Box xcss={styles.iconContainer}>
				<StatusIndicator status={status} />
				{!isLastItem && <Box xcss={styles.verticalRule} />}
			</Box>
			<Box xcss={styles.contentContainer}>{children}</Box>
		</Flex>
	);
};

export const LoadingStepTitle = ({ title }: { title?: string }) => {
	return (
		<Box xcss={styles.titleContainer}>
			<Text size="medium" color="color.text.subtle">
				{title}
			</Text>
		</Box>
	);
};

export const LoadingStep = ({
	title,
	testId = 'rovo-loading-step',
	isLastItem = false,
	status,
	placeholder,
}: LoadingStepProps) => {
	return (
		<LoadingStepContainer testId={testId} status={status} isLastItem={isLastItem}>
			<Flex direction="row">
				<LoadingStepTitle title={title} />
				{placeholder && <Box xcss={styles.loadingStepIconPlaceholder} />}
			</Flex>
		</LoadingStepContainer>
	);
};
